// ============================================
// UTILITY FUNCTIONS
// ============================================

import {
  TravelItem,
  WorkItem,
  PartItem,
  CustomLineItem,
  CalculatedTotals,
  SavedOffer,
  SavedWorkOrder,
  WorkOrderFormData,
  StorageCounters,
  AppSettings,
  CountryCode,
  SavedAnsulCommissioning
} from '../types';
import { defaultSettings, vatRates, currencyCodes, localeMapping } from '../config';

// ============================================
// FORMATTING
// ============================================

/**
 * Format currency based on country code
 * @param amount - The amount to format
 * @param country - Country code (fi, se, no, etc.) - defaults to 'fi'
 */
export const formatCurrency = (amount: number, country: CountryCode = 'fi'): string => {
  const currency = currencyCodes[country] || 'EUR';
  const locale = localeMapping[country] || 'fi-FI';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Get currency symbol for a country
 */
export const getCurrencySymbol = (country: CountryCode): string => {
  const symbols: Record<string, string> = {
    EUR: '€',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    GBP: '£',
    TRY: '₺',
  };
  return symbols[currencyCodes[country]] || '€';
};

export const formatDate = (date: string | Date, locale: string = 'fi-FI'): string => {
  return new Intl.DateTimeFormat(locale).format(new Date(date));
};

export const formatDateTime = (date: string | Date, locale: string = 'fi-FI'): string => {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(date));
};

export const formatPercent = (value: number): string => {
  return `${value}%`;
};

// ============================================
// CALCULATIONS
// ============================================

export const calculateTravelCost = (travel: TravelItem, _settings: AppSettings): number => {
  // Sanitize numeric inputs
  const distance = Number(travel.distance) || 0;
  const travelTime = Number(travel.travelTime) || 0;
  const timeRate = Number(travel.timeRate) || 0;
  const distanceRate = Number(travel.distanceRate) || 0;
  const fixedPrice = Number(travel.fixedPrice) || 0;
  const multiplier = Number(travel.tripMultiplier) || 1;
  const discount = Math.min(100, Math.max(0, Number(travel.discountPercent) || 0));

  let baseCost: number;

  // Choose calculation based on mode
  if (travel.mode === 'fixed') {
    // Fixed price mode (not affected by multiplier)
    baseCost = fixedPrice;
  } else {
    // Per-km mode: (travelTime * timeRate + distance * distanceRate) * tripMultiplier
    baseCost = ((travelTime * timeRate) + (distance * distanceRate)) * multiplier;
  }

  // Apply discount
  const discountedCost = baseCost * (1 - discount / 100);

  return Math.max(0, discountedCost);
};

export const calculateLaborCost = (work: WorkItem): number => {
  // Sanitize numeric inputs
  const hours = Number(work.hours) || 0;
  const technicians = Number(work.technicians) || 0;
  const hourlyRate = Number(work.hourlyRate) || 0;
  const discount = Math.min(100, Math.max(0, Number(work.discountPercent) || 0));

  let laborCost = hours * technicians * hourlyRate;

  // Apply surcharges
  if (work.nightSunday) laborCost *= 1.5;
  if (work.urgent) laborCost = laborCost * 1.5 + 50;

  // Apply discount if present
  if (discount > 0) {
    laborCost = laborCost * (1 - discount / 100);
  }

  return Math.max(0, laborCost);
};

export const calculateAccommodationCost = (work: WorkItem, settings: AppSettings): number => {
  // Sanitize numeric inputs
  const nights = Number(work.accommodationNights) || 0;
  const accommodationRate = Number(settings.accommodationRate) || 0;
  const technicians = Number(work.technicians) || 0;
  const perDiemDays = Number(work.perDiemDays) || 0;
  const perDiemRate = Number(settings.perDiemRate) || 0;
  const discount = Math.min(100, Math.max(0, Number(work.discountPercent) || 0));

  const accommodation = nights * accommodationRate * technicians;
  const perDiem = perDiemDays * perDiemRate * technicians;
  let total = accommodation + perDiem;

  // Apply same discount to accommodation/per diem
  if (discount > 0) {
    total = total * (1 - discount / 100);
  }

  return Math.max(0, total);
};

export const calculateCallOutFee = (work: WorkItem): number => {
  let fee = work.callOutFee === -1 ? (Number(work.customCallOutFee) || 0) : (Number(work.callOutFee) || 0);

  // Apply discount to call-out fee as well
  const discount = Math.min(100, Math.max(0, Number(work.discountPercent) || 0));
  if (discount > 0) {
    fee = fee * (1 - discount / 100);
  }

  return Math.max(0, fee);
};

export const calculatePartCost = (part: PartItem): number => {
  // Sanitize numeric inputs
  const quantity = Number(part.quantity) || 0;
  const unitPrice = Number(part.unitPrice) || 0;
  const markup = Number(part.markup) || 0;
  const discount = Math.min(100, Math.max(0, Number(part.discountPercent) || 0));

  let cost = quantity * unitPrice * (1 + markup / 100);

  // Apply discount after markup
  if (discount > 0) {
    cost = cost * (1 - discount / 100);
  }

  return Math.max(0, cost);
};

export const calculateTotals = (
  travelItems: TravelItem[],
  workItems: WorkItem[],
  parts: PartItem[],
  customLines: CustomLineItem[],
  settings: AppSettings
): CalculatedTotals => {
  // Travel costs
  const travelCost = travelItems.reduce((sum, item) => sum + calculateTravelCost(item, settings), 0);

  // Labor costs
  const laborCost = workItems.reduce((sum, item) => sum + calculateLaborCost(item), 0);

  // Call-out fees
  const callOutFee = workItems.reduce((sum, item) => sum + calculateCallOutFee(item), 0);

  // Accommodation & per diem
  const accommodationCost = workItems.reduce((sum, item) => sum + calculateAccommodationCost(item, settings), 0);

  // Parts
  const partsCost = parts.reduce((sum, item) => sum + calculatePartCost(item), 0);

  // Custom lines
  const customLinesCost = customLines.reduce((sum, item) => sum + item.price * (1 - (item.discountPercent || 0) / 100), 0);

  // Totals
  const subtotal = travelCost + laborCost + callOutFee + accommodationCost + partsCost + customLinesCost;
  const vatRate = vatRates[settings.companyCountry];
  const vat = subtotal * vatRate;
  const total = subtotal + vat;

  return {
    subtotal,
    vat,
    vatRate,
    vatPercent: vatRate * 100,
    total,
    breakdown: {
      travel: travelCost,
      labor: laborCost,
      accommodation: accommodationCost,
      parts: partsCost,
      callOutFee,
      customLines: customLinesCost,
    },
  };
};

// ============================================
// LOCAL STORAGE
// ============================================

const STORAGE_KEYS = {
  OFFERS: 'halton_v2_offers',
  WORK_ORDERS: 'halton_v2_work_orders',
  MAINTENANCE_REPORTS: 'halton_v2_maintenance_reports',
  ANSUL_COMMISSIONINGS: 'halton_v2_ansul_commissionings',
  COUNTERS: 'halton_v2_counters',
  SETTINGS: 'halton_v2_settings',
};

// Settings
export const loadSettings = (): AppSettings => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (data) {
      return { ...defaultSettings, ...JSON.parse(data) };
    }
  } catch (error) {
    console.error('Failed to load settings:', error);
  }
  return defaultSettings;
};

export const saveSettings = (settings: AppSettings): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save settings:', error);
  }
};

// Counters
export const loadCounters = (): StorageCounters => {
  const currentYear = new Date().getFullYear();
  const defaults: StorageCounters = {
    offerCounter: 1,
    workOrderCounter: 1,
    maintenanceReportCounter: 1,
    ansulCommissioningCounter: 1,
    jobNumberCounter: 1,
    jobNumberYear: currentYear,
  };
  try {
    const data = localStorage.getItem(STORAGE_KEYS.COUNTERS);
    if (data) {
      const parsed = JSON.parse(data);
      // Merge with defaults to handle missing fields from older versions
      return { ...defaults, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load counters:', error);
  }
  return defaults;
};

export const saveCounters = (counters: StorageCounters): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.COUNTERS, JSON.stringify(counters));
  } catch (error) {
    console.error('Failed to save counters:', error);
  }
};

export const generateOfferNumber = (): string => {
  const counters = loadCounters();
  const year = new Date().getFullYear();
  const number = String(counters.offerCounter).padStart(4, '0');
  counters.offerCounter++;
  saveCounters(counters);
  return `T-${year}-${number}`;
};

export const generateWorkOrderNumber = (): string => {
  const counters = loadCounters();
  const year = new Date().getFullYear();
  const number = String(counters.workOrderCounter).padStart(4, '0');
  counters.workOrderCounter++;
  saveCounters(counters);
  return `TT-${year}-${number}`;
};

export const generateMaintenanceReportNumber = (): string => {
  const counters = loadCounters();
  const year = new Date().getFullYear();
  const number = String(counters.maintenanceReportCounter || 1).padStart(4, '0');
  counters.maintenanceReportCounter = (counters.maintenanceReportCounter || 1) + 1;
  saveCounters(counters);
  return `HR-${year}-${number}`;
};

/**
 * Generate a job number and increment the counter.
 * Format: XYY-NNN where X=user identity (1-9), YY=year (last 2 digits), NNN=sequential counter
 * Counter resets to 001 at the start of each new year.
 * @param userIdentity - User identity number (1-9)
 * @returns Generated job number (e.g., "226-001")
 */
export const generateJobNumber = (userIdentity: number): string => {
  const counters = loadCounters();
  const currentYear = new Date().getFullYear();
  const yearLast2Digits = String(currentYear).slice(-2);

  // Check if year has changed - reset counter if so
  if (counters.jobNumberYear !== currentYear) {
    counters.jobNumberCounter = 1;
    counters.jobNumberYear = currentYear;
  }

  // Generate the job number
  const jobNumber = `${userIdentity}${yearLast2Digits}-${String(counters.jobNumberCounter).padStart(3, '0')}`;

  // Increment counter and save
  counters.jobNumberCounter++;
  saveCounters(counters);

  return jobNumber;
};

/**
 * Preview the next job number without incrementing the counter.
 * Useful for showing what the next job number will be before committing.
 * @param userIdentity - User identity number (1-9)
 * @returns Preview of next job number (e.g., "226-001")
 */
export const getNextJobNumber = (userIdentity: number): string => {
  const counters = loadCounters();
  const currentYear = new Date().getFullYear();
  const yearLast2Digits = String(currentYear).slice(-2);

  // Check if year would change - use counter 1 if so
  let counter = counters.jobNumberCounter;
  if (counters.jobNumberYear !== currentYear) {
    counter = 1;
  }

  return `${userIdentity}${yearLast2Digits}-${String(counter).padStart(3, '0')}`;
};

// Offers
export const loadOffers = (): SavedOffer[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.OFFERS);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    console.error('Failed to load offers:', error);
  }
  return [];
};

export const saveOffers = (offers: SavedOffer[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.OFFERS, JSON.stringify(offers));
  } catch (error) {
    console.error('Failed to save offers:', error);
  }
};

export const saveOffer = (offer: SavedOffer): void => {
  const offers = loadOffers();
  const existingIndex = offers.findIndex(o => o.id === offer.id);

  if (existingIndex >= 0) {
    offers[existingIndex] = { ...offer, updatedAt: new Date().toISOString() };
  } else {
    offers.push(offer);
  }

  saveOffers(offers);
};

export const updateOffer = (offer: SavedOffer): void => {
  saveOffer(offer);
};

export const deleteOffer = (id: string): void => {
  const offers = loadOffers();
  saveOffers(offers.filter(o => o.id !== id));
};

// Work Orders
export const loadWorkOrders = (): SavedWorkOrder[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.WORK_ORDERS);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    console.error('Failed to load work orders:', error);
  }
  return [];
};

export const saveWorkOrders = (workOrders: SavedWorkOrder[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.WORK_ORDERS, JSON.stringify(workOrders));
  } catch (error) {
    console.error('Failed to save work orders:', error);
  }
};

export const saveWorkOrder = (workOrder: SavedWorkOrder): void => {
  const workOrders = loadWorkOrders();
  const existingIndex = workOrders.findIndex(wo => wo.id === workOrder.id);

  if (existingIndex >= 0) {
    workOrders[existingIndex] = { ...workOrder, updatedAt: new Date().toISOString() };
  } else {
    workOrders.push(workOrder);
  }

  saveWorkOrders(workOrders);
};

export const updateWorkOrder = (workOrder: SavedWorkOrder): void => {
  saveWorkOrder(workOrder);
};

export const deleteWorkOrder = (id: string): void => {
  const workOrders = loadWorkOrders();
  saveWorkOrders(workOrders.filter(wo => wo.id !== id));
};

// Maintenance Reports
export const loadMaintenanceReports = (): any[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.MAINTENANCE_REPORTS);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    console.error('Failed to load maintenance reports:', error);
  }
  return [];
};

export const saveMaintenanceReports = (reports: any[]): void => {
  try {
    // Remove images from reports before saving to localStorage to save space
    // We already handle this in the component but doing it here as a safety measure
    // for bulk saves
    const sanitizedReports = reports.map(r => {
      if ((r.generalImages?.length > 0 || r.beforeAfterImages?.length > 0) && !r.imagesExcluded) {
        return { ...r, generalImages: [], beforeAfterImages: [], imagesExcluded: true };
      }
      return r;
    });
    localStorage.setItem(STORAGE_KEYS.MAINTENANCE_REPORTS, JSON.stringify(sanitizedReports));
  } catch (error) {
    console.error('Failed to save maintenance reports:', error);
  }
};

export const saveMaintenanceReport = (report: any): void => {
  const reports = loadMaintenanceReports();
  const existingIndex = reports.findIndex((r: any) => r.id === report.id);

  // Ensure images are not saved to localStorage
  const reportToSave = {
    ...report,
    generalImages: [],
    beforeAfterImages: [],
    imagesExcluded: true,
    updatedAt: new Date().toISOString()
  };

  if (existingIndex >= 0) {
    reports[existingIndex] = reportToSave;
  } else {
    reports.push(reportToSave);
  }

  saveMaintenanceReports(reports);
};

export const updateMaintenanceReport = (report: any): void => {
  saveMaintenanceReport(report);
};

export const deleteMaintenanceReport = (id: string): void => {
  const reports = loadMaintenanceReports();
  saveMaintenanceReports(reports.filter((r: any) => r.id !== id));
};

// Create Work Order from Offer
export const createWorkOrderFromOffer = (offer: SavedOffer): Partial<WorkOrderFormData> => {
  // Determine address mapping based on addressType
  const addressType = offer.addressType || 'both';
  const useBillingAddress = addressType === 'billing' || addressType === 'both';
  const useSiteAddress = addressType === 'site' || addressType === 'both';

  return {
    // Map customer info to billing (if billing address type selected)
    yritys: offer.customerName,
    osoite: useBillingAddress ? offer.customerAddress : '',

    // Map to site info (if site address type selected)
    kohde: offer.customerName,
    kohdeOsoite: useSiteAddress ? offer.customerAddress : '',

    // Set offer reference
    viite: offer.documentNumber,

    // Job number from offer
    jobNumber: offer.jobNumber || '',

    // Use customerNotes as work description (user-written notes for customer)
    kuvaus: offer.customerNotes || '',

    // Leave required fields empty for user to fill
    yhteyshenkilo: '',
    sahkoposti: '',
    puhelin: '',
    ytunnus: '',
    tilausnumero: '',
    ajankohta: '',

    // New billing fields - empty for user to fill
    tilaaja: '',
    laskutusosoite: '',
    verkkolaskuosoite: '',
    verkkolaskuoperaattori: '',

    // Optional fields
    postinumero: '',
    paikkakunta: '',
    kohdePostinumero: '',
    kohdePaikkakunta: '',
    asiakasnumero: '',
    ovt: '',
    operaattori: '',
    merkki: '',
    lineOfBusiness: '',
    tuoteryhma: '',
    lisatietoja: '',
    teknikonNimi: '',
    paivamaara: '',
  };
};

// Export/Import
export const exportData = (): string => {
  const data = {
    offers: loadOffers(),
    workOrders: loadWorkOrders(),
    maintenanceReports: loadMaintenanceReports(),
    ansulCommissionings: loadAnsulCommissionings(),
    counters: loadCounters(),
    settings: loadSettings(),
    exportDate: new Date().toISOString(),
    version: '2.0.0',
  };
  return JSON.stringify(data, null, 2);
};

export const importData = (jsonString: string): boolean => {
  try {
    const data = JSON.parse(jsonString);

    if (data.offers) saveOffers(data.offers);
    if (data.workOrders) saveWorkOrders(data.workOrders);
    if (data.maintenanceReports) saveMaintenanceReports(data.maintenanceReports);
    if (data.ansulCommissionings) saveAnsulCommissionings(data.ansulCommissionings);
    if (data.counters) saveCounters(data.counters);
    if (data.settings) saveSettings(data.settings);

    return true;
  } catch (error) {
    console.error('Import failed:', error);
    return false;
  }
};

// ============================================
// ID GENERATION
// ============================================

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// ============================================
// DEFAULT ITEM CREATORS
// ============================================

export const createDefaultTravelItem = (settings: AppSettings): TravelItem => ({
  id: Date.now(),
  mode: 'perKm',
  distance: 0,
  travelTime: 1,
  timeRate: settings.defaultTimeRate,
  distanceRate: settings.defaultDistanceRate,
  clientsOnTrip: 1,
  discountPercent: 0,
  fixedPrice: 0,
  tripMultiplier: 1,
});

export const createDefaultWorkItem = (settings: AppSettings): WorkItem => ({
  id: Date.now(),
  technicians: 1,
  hours: 0,
  hourlyRate: settings.defaultHourlyRate,
  callOutFee: 0,
  customCallOutFee: 0,
  nightSunday: false,
  urgent: false,
  accommodationNights: 0,
  perDiemDays: 0,
});

export const createDefaultPartItem = (): PartItem => ({
  id: Date.now(),
  description: '',
  quantity: 1,
  unitPrice: 0,
  markup: 0,
});

export const createDefaultCustomLineItem = (): CustomLineItem => ({
  id: Date.now(),
  name: '',
  description: '',
  price: 0,
});

// ============================================
// DEMO DATA CREATION
// ============================================

export const createDemoOffer = (): void => {
  const travelItems: TravelItem[] = [
    {
      id: Date.now() + 1,
      mode: 'perKm',
      distance: 45,
      travelTime: 1.5,
      timeRate: 65,
      distanceRate: 0.85,
      clientsOnTrip: 2,
      discountPercent: 0,
      fixedPrice: 0,
      tripMultiplier: 1,
    }
  ];

  const workItems: WorkItem[] = [
    {
      id: Date.now() + 2,
      technicians: 2,
      hours: 6,
      hourlyRate: 85,
      callOutFee: 0,
      customCallOutFee: 0,
      nightSunday: false,
      urgent: false,
      accommodationNights: 0,
      perDiemDays: 0,
    },
    {
      id: Date.now() + 3,
      technicians: 1,
      hours: 2,
      hourlyRate: 85,
      callOutFee: 0,
      customCallOutFee: 0,
      nightSunday: false,
      urgent: false,
      accommodationNights: 0,
      perDiemDays: 0,
    }
  ];

  const parts: PartItem[] = [
    {
      id: Date.now() + 4,
      description: 'UV-lamppu UVC 55W TUV PL-L',
      quantity: 4,
      unitPrice: 125.50,
      markup: 15,
    },
    {
      id: Date.now() + 5,
      description: 'Suodatin M5 600x600mm',
      quantity: 6,
      unitPrice: 38.90,
      markup: 15,
    },
    {
      id: Date.now() + 6,
      description: 'Hihna A43',
      quantity: 2,
      unitPrice: 24.50,
      markup: 15,
    }
  ];

  const customLines: CustomLineItem[] = [
    {
      id: Date.now() + 7,
      name: 'Lisätyö',
      description: 'Sähköliityntöjen tarkistus ja korjaus',
      price: 150,
    }
  ];

  const totals = calculateTotals(travelItems, workItems, parts, customLines, defaultSettings);

  const demoOffer: SavedOffer = {
    id: `demo-offer-${Date.now()}`,
    documentNumber: generateOfferNumber(),
    customerName: 'Esimerkki Asiakas Oy',
    customerAddress: 'Esimerkkikatu 123\n00100 Helsinki',
    customerNotes: 'Huomioitavaa: Työ tehdään aukioloaikojen ulkopuolella. Tarvitaan kulkuluvat ja turvallisuusperehdytys.',
    travelItems,
    workItems,
    parts,
    customLines,
    totals,
    settings: defaultSettings,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  saveOffer(demoOffer);
};

export const createDemoWorkOrder = (): void => {
  // First calculate totals for pricing items
  const travelItems: TravelItem[] = [
    {
      id: Date.now() + 10,
      mode: 'perKm',
      distance: 35,
      travelTime: 1,
      timeRate: 65,
      distanceRate: 0.85,
      clientsOnTrip: 2,
      discountPercent: 0,
      fixedPrice: 0,
      tripMultiplier: 1,
    }
  ];

  const workItems: WorkItem[] = [
    {
      id: Date.now() + 11,
      technicians: 2,
      hours: 5,
      hourlyRate: 85,
      callOutFee: 0,
      customCallOutFee: 0,
      nightSunday: false,
      urgent: false,
      accommodationNights: 0,
      perDiemDays: 0,
    }
  ];

  const parts: PartItem[] = [
    {
      id: Date.now() + 12,
      description: 'Rasvasuodatin 500x500mm',
      quantity: 8,
      unitPrice: 42.50,
      markup: 15,
    },
    {
      id: Date.now() + 13,
      description: 'UV-lamppu 36W',
      quantity: 4,
      unitPrice: 98.00,
      markup: 15,
    }
  ];

  const customLines: CustomLineItem[] = [];

  const totals = calculateTotals(travelItems, workItems, parts, customLines, defaultSettings);

  const demoWorkOrder: SavedWorkOrder = {
    id: `demo-workorder-${Date.now()}`,
    documentNumber: generateWorkOrderNumber(),
    formData: {
      yhteyshenkilo: 'Matti Meikäläinen',
      sahkoposti: 'matti.meikalainen@esimerkki.fi',
      puhelin: '+358 40 123 4567',
      yritys: 'Esimerkki Ravintola Oy',
      asiakasnumero: 'AS-12345',
      ytunnus: '1234567-8',
      osoite: 'Ravintolatantie 45',
      postinumero: '00200',
      paikkakunta: 'Helsinki',
      ovt: '003712345678',
      operaattori: 'BAWCFI22',
      viite: 'HUOLTO-2025-001',
      tilaaja: 'Mikko Ravintolasema',
      laskutusosoite: 'PL 123, 00101 Helsinki',
      verkkolaskuosoite: '003712345678',
      verkkolaskuoperaattori: 'BAWCFI22',
      tilausnumero: 'PO-2025-0789',
      jobNumber: 'JOB-2025-001',
      ourReference: 'Halton Service / Pekka Teknikko',
      kohde: 'Ravintolan pääkeittiö, 2. kerros',
      kohdeOsoite: 'Ravintolatantie 45',
      kohdePostinumero: '00200',
      kohdePaikkakunta: 'Helsinki',
      ajankohta: '15.01.2025 klo 07:00-16:00',
      kuvaus: 'Keittiön kattoliesituulettimien huolto ja UV-järjestelmän tarkistus. Työ sisältää:\n- Suodattimien vaihto\n- UV-lamppujen vaihto\n- Puhallinten puhdistus ja voitelu\n- Ilmamäärien mittaus ja säätö\n- Toimintakokeet',
      merkki: '',
      lineOfBusiness: 'service',
      tuoteryhma: 'Keittiöilmanvaihto',
      lisatietoja: 'Työ tehdään ravintolan sulkemisaikana. Tarvitaan kulkuluvat ja hygieniapassi. Työhön varattu 2 asentajaa.',
      teknikonNimi: 'Pekka Teknikko',
      paivamaara: new Date().toISOString().split('T')[0],
    },
    travelItems,
    workItems,
    parts,
    customLines,
    totals,
    settings: {
      printLanguage: defaultSettings.printLanguage,
      companyCountry: defaultSettings.companyCountry,
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  saveWorkOrder(demoWorkOrder);
};

export const createDemoMaintenanceReport = (): void => {
  const demoReport = {
    id: `demo-report-${Date.now()}`,
    documentNumber: generateMaintenanceReportNumber(),
    type: 'kcw_annual',
    userId: 'user-1',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'draft',
    formData: {
      customerName: 'Demo Asiakas Oy',
      siteName: 'Helsingin Pääkonttori',
      date: new Date().toISOString().split('T')[0],
      technician: 'Matti Meikäläinen',
      type: 'kcw_annual'
    },
    checklist: [
      { id: '1', text: 'HMI-kosketusnäytön tilan tarkistus (hälytykset, kuittaus, päivitys)', status: 'ok', notes: '' },
      { id: '2', text: 'Järjestelmän toiminnan testaus (Test-painike)', status: 'ok', notes: '' },
      { id: '3', text: 'UV-lamppujen toiminnan tarkistus', status: 'nok', notes: 'Lamppu 3 välkkyy, vaihdettava ensi kerralla' },
      { id: '4', text: 'Paine-erokytkimien tarkistus', status: 'ok', notes: '' },
      { id: '5', text: 'Kammion ja suodattimien puhtauden tarkistus', status: 'na', notes: 'Ei pääsyä kammioon' }
    ],
    images: [],
    imagesExcluded: true
  };

  saveMaintenanceReport(demoReport);
};

// ============================================
// EXPORT / IMPORT FUNCTIONS
// ============================================

export interface ExportData {
  exportVersion: string;
  exportDate: string;
  exportedBy: string;
  workOrders?: SavedWorkOrder[];
  offers?: SavedOffer[];
  ansulCommissionings?: SavedAnsulCommissioning[];
}

/**
 * Generate filename for export based on job numbers
 * Job Number format: 126-001 (1=User ID, 26=Year, 001=Job number)
 */
const generateExportFilename = (workOrders: SavedWorkOrder[], offers: SavedOffer[]): string => {
  const date = new Date().toISOString().split('T')[0];
  
  // Collect job numbers from work orders
  const jobNumbers = workOrders
    .map(wo => wo.formData.jobNumber)
    .filter(jn => jn && jn.trim() !== '');
  
  // Collect document numbers from offers (as they don't have job numbers)
  const offerNumbers = offers
    .map(o => o.documentNumber)
    .filter(dn => dn && dn.trim() !== '');
  
  if (jobNumbers.length === 1 && offerNumbers.length === 0) {
    // Single work order with job number
    return `workorder-${jobNumbers[0]}.json`;
  } else if (jobNumbers.length === 0 && offerNumbers.length === 1) {
    // Single offer
    return `quote-${offerNumbers[0]}.json`;
  } else if (jobNumbers.length > 0) {
    // Multiple items - use first job number + count
    return `export-${jobNumbers[0]}-${date}.json`;
  } else if (offerNumbers.length > 0) {
    // Multiple offers only
    return `quotes-export-${date}.json`;
  } else {
    // Fallback
    return `halton-export-${date}.json`;
  }
};

/**
 * Export work orders and/or offers to a JSON file for sharing
 */
export const exportDocuments = (workOrders: SavedWorkOrder[], offers: SavedOffer[]): void => {
  const exportData: ExportData = {
    exportVersion: '1.0',
    exportDate: new Date().toISOString(),
    exportedBy: 'Halton Service Laama v2.0',
  };
  
  if (workOrders.length > 0) {
    exportData.workOrders = workOrders;
  }
  
  if (offers.length > 0) {
    exportData.offers = offers;
  }

  const ansulCommissionings = loadAnsulCommissionings();
  if (ansulCommissionings.length > 0) {
    exportData.ansulCommissionings = ansulCommissionings;
  }
  
  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const filename = generateExportFilename(workOrders, offers);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export a single work order
 */
export const exportSingleWorkOrder = (workOrder: SavedWorkOrder): void => {
  exportDocuments([workOrder], []);
};

/**
 * Export a single offer/quote
 */
export const exportSingleOffer = (offer: SavedOffer): void => {
  exportDocuments([], [offer]);
};

export interface ImportResult {
  success: boolean;
  workOrdersImported: number;
  offersImported: number;
  ansulCommissioningsImported: number;
  ansulCommissioningsSkipped: number;
  workOrdersSkipped: number;
  offersSkipped: number;
  errors: string[];
}

/**
 * Import documents from a JSON file
 * @param file - The file to import
 * @param mode - 'merge' to add only new items, 'replace' to overwrite existing
 */
export const importDocuments = async (file: File, mode: 'merge' | 'replace' = 'merge'): Promise<ImportResult> => {
  const result: ImportResult = {
    success: false,
    workOrdersImported: 0,
    offersImported: 0,
    ansulCommissioningsImported: 0,
    ansulCommissioningsSkipped: 0,
    workOrdersSkipped: 0,
    offersSkipped: 0,
    errors: [],
  };
  
  try {
    const text = await file.text();
    const data = JSON.parse(text) as ExportData;
    
    // Validate export format
    if (!data.exportVersion || !data.exportedBy) {
      result.errors.push('Invalid file format: missing export metadata');
      return result;
    }
    
    // Import work orders
    if (data.workOrders && Array.isArray(data.workOrders)) {
      const existingWorkOrders = loadWorkOrders();
      const existingIds = new Set(existingWorkOrders.map(wo => wo.id));
      const existingDocNumbers = new Set(existingWorkOrders.map(wo => wo.documentNumber));
      
      for (const wo of data.workOrders) {
        // Check for duplicates
        if (mode === 'merge' && (existingIds.has(wo.id) || existingDocNumbers.has(wo.documentNumber))) {
          result.workOrdersSkipped++;
          continue;
        }
        
        // If replacing, generate new ID to avoid conflicts
        const importedWo: SavedWorkOrder = {
          ...wo,
          id: mode === 'replace' ? `imported-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` : wo.id,
          updatedAt: new Date().toISOString(),
        };
        
        saveWorkOrder(importedWo);
        result.workOrdersImported++;
      }
    }
    
    // Import offers
    if (data.offers && Array.isArray(data.offers)) {
      const existingOffers = loadOffers();
      const existingIds = new Set(existingOffers.map(o => o.id));
      const existingDocNumbers = new Set(existingOffers.map(o => o.documentNumber));
      
      for (const offer of data.offers) {
        // Check for duplicates
        if (mode === 'merge' && (existingIds.has(offer.id) || existingDocNumbers.has(offer.documentNumber))) {
          result.offersSkipped++;
          continue;
        }
        
        // If replacing, generate new ID to avoid conflicts
        const importedOffer: SavedOffer = {
          ...offer,
          id: mode === 'replace' ? `imported-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` : offer.id,
          updatedAt: new Date().toISOString(),
        };
        
        saveOffer(importedOffer);
        result.offersImported++;
      }
    }
    
    // Import Ansul commissionings
    if (data.ansulCommissionings && Array.isArray(data.ansulCommissionings)) {
      const existingCommissionings = loadAnsulCommissionings();
      const existingIds = new Set(existingCommissionings.map(c => c.id));
      const existingDocNumbers = new Set(existingCommissionings.map(c => c.documentNumber));
      
      for (const commissioning of data.ansulCommissionings) {
        // Check for duplicates
        if (mode === 'merge' && (existingIds.has(commissioning.id) || existingDocNumbers.has(commissioning.documentNumber))) {
          result.ansulCommissioningsSkipped++;
          continue;
        }
        
        // If replacing, generate new ID to avoid conflicts
        const importedCommissioning: SavedAnsulCommissioning = {
          ...commissioning,
          id: mode === 'replace' ? `imported-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` : commissioning.id,
          updatedAt: new Date().toISOString(),
        };
        
        saveAnsulCommissioningItem(importedCommissioning);
        result.ansulCommissioningsImported++;
      }
    }
    
    result.success = true;
  } catch (error) {
    result.errors.push(`Failed to parse file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
  
  return result;
};

// ============================================
// ANSUL COMMISSIONINGS
// ============================================

// Migration helper — moves data from legacy key to centralized key
const migrateAnsulCommissionings = (): SavedAnsulCommissioning[] => {
  const LEGACY_KEY = 'halton_ansul_commissionings';
  try {
    const legacyData = localStorage.getItem(LEGACY_KEY);
    if (legacyData) {
      const parsed: SavedAnsulCommissioning[] = JSON.parse(legacyData);
      // Stamp migrated reports with reportType
      const stamped = parsed.map(c => ({ ...c, reportType: c.reportType || ('ansul_r102' as const) }));
      localStorage.setItem(STORAGE_KEYS.ANSUL_COMMISSIONINGS, JSON.stringify(stamped));
      localStorage.removeItem(LEGACY_KEY);
      return stamped;
    }
  } catch (error) {
    console.error('Failed to migrate Ansul commissionings:', error);
  }
  return [];
};

export const loadAnsulCommissionings = (): SavedAnsulCommissioning[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.ANSUL_COMMISSIONINGS);
    if (data) {
      const commissionings: SavedAnsulCommissioning[] = JSON.parse(data);
      // Stamp existing reports without reportType
      let needsSave = false;
      const stamped = commissionings.map(c => {
        if (!c.reportType) {
          needsSave = true;
          return { ...c, reportType: 'ansul_r102' as const };
        }
        return c;
      });
      if (needsSave) {
        localStorage.setItem(STORAGE_KEYS.ANSUL_COMMISSIONINGS, JSON.stringify(stamped));
      }
      return stamped;
    }
  } catch (error) {
    console.error('Failed to load Ansul commissionings:', error);
  }
  // Try migration from legacy key
  return migrateAnsulCommissionings();
};

export const saveAnsulCommissionings = (commissionings: SavedAnsulCommissioning[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.ANSUL_COMMISSIONINGS, JSON.stringify(commissionings));
  } catch (error) {
    console.error('Failed to save Ansul commissionings:', error);
  }
};

export const saveAnsulCommissioningItem = (commissioning: SavedAnsulCommissioning): void => {
  const commissionings = loadAnsulCommissionings();
  const existingIndex = commissionings.findIndex(c => c.id === commissioning.id);
  if (existingIndex >= 0) {
    commissionings[existingIndex] = { ...commissioning, updatedAt: new Date().toISOString() };
  } else {
    commissionings.push(commissioning);
  }
  saveAnsulCommissionings(commissionings);
};

export const deleteAnsulCommissioning = (id: string): void => {
  const commissionings = loadAnsulCommissionings();
  saveAnsulCommissionings(commissionings.filter(c => c.id !== id));
};

export const generateAnsulCommissioningNumber = (): string => {
  const counters = loadCounters();
  const year = new Date().getFullYear();
  const number = String(counters.ansulCommissioningCounter).padStart(4, '0');
  counters.ansulCommissioningCounter++;
  saveCounters(counters);
  return `AC-${year}-${number}`;
};
